import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signInWithRedirect, signOut as amplifySignOut, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useNavigate } from 'react-router-dom';
import { isDevBypassAuthEnabled, DEV_BYPASS_USER_ID } from '../utils/auth-utils';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any;
    signIn: () => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [user, setUser] = useState<any>(null);
    const navigate = useNavigate();

    useEffect(() => {
        checkAuthState();

        // Set up listener for auth events using Amplify Hub
        const hubListener = Hub.listen('auth', ({ payload }) => {
            const { event } = payload;

            if (event === 'signedIn') {
                checkAuthState();
                navigate('/');
            } else if (event === 'signedOut') {
                setIsAuthenticated(false);
                setUser(null);
                navigate('/login');
            }
        });

        // Clean up the listener when the component unmounts
        return () => {
            hubListener();
        };
    }, [navigate]);

    const checkAuthState = async () => {
        try {
            setIsLoading(true);
            if (isDevBypassAuthEnabled()) {
                setIsAuthenticated(true);
                setUser({ userId: DEV_BYPASS_USER_ID, username: 'dev-user' });
                return;
            }
            // Use Amplify's getCurrentUser to check if the user is authenticated
            const currentUser = await getCurrentUser();
            setIsAuthenticated(true);
            setUser(currentUser);
        } catch (error) {
            // If getCurrentUser throws an error, the user is not authenticated
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const signIn = async () => {
        try {
            if (isDevBypassAuthEnabled()) {
                setIsAuthenticated(true);
                setUser({ userId: DEV_BYPASS_USER_ID, username: 'dev-user' });
                navigate('/');
                return;
            }
            // Use Amplify's signInWithRedirect to handle federated sign-in
            // This will automatically redirect to the configured OIDC provider (Microsoft)
            await signInWithRedirect({
                provider: {
                    custom: process.env.REACT_APP_OIDC_IDP_NAME || '' // Use the name of your OIDC provider
                }
            });
        } catch (error) {
            console.error('Error signing in:', error);
            throw error;
        }
    };

    const signOut = async () => {
        try {
            if (!isDevBypassAuthEnabled()) {
                await amplifySignOut();
            }
            setIsAuthenticated(false);
            setUser(null);
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    };

    const value = {
        isAuthenticated,
        isLoading,
        user,
        signIn,
        signOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
